import { FacilityStatusEnum } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import {
  useGetWorkplacesById,
  type WorkplaceData,
} from "@src/appV2/redesign/Workplace/useGetWorkplacesById";
import { useMemo } from "react";

import { type ChatChannel } from "../../types";

/**
 * Workplace statuses that should be filtered out from the chat list.
 * Chats for workplaces with these statuses will not be shown to workers.
 */
const INACTIVE_WORKPLACE_STATUSES = new Set<string>([
  FacilityStatusEnum.SUSPENDED,
  FacilityStatusEnum.TERMINATED,
  FacilityStatusEnum.ARCHIVED,
]);

/**
 * Extracts the workplace ID from a chat channel URL.
 * Channel URLs follow the format: `${workplaceId}_${workerId}` for flex chats
 * and `${workplaceId}_${workerId}_${placementId}` for placement chats.
 */
function getWorkplaceIdFromChannel<T extends ChatChannel>(channel: T): string | undefined {
  const [workplaceId] = channel.url.split("_");
  return workplaceId || undefined;
}

/**
 * Extracts unique workplace IDs from a list of chat channels.
 */
function getUniqueWorkplaceIds<T extends ChatChannel>(channels: T[]): string[] {
  const workplaceIds = channels
    .map((channel) => getWorkplaceIdFromChannel(channel))
    .filter(isDefined);

  return [...new Set(workplaceIds)];
}

/**
 * Creates a map of workplace ID to workplace status for quick lookup.
 */
function createWorkplaceStatusMap(workplaces: WorkplaceData[]): Map<string, string> {
  const statusMap = new Map<string, string>();

  for (const workplace of workplaces) {
    statusMap.set(workplace.id, workplace.attributes.status);
  }

  return statusMap;
}

/**
 * Checks if a workplace has an inactive status (SUSPENDED, TERMINATED, or ARCHIVED).
 */
function isWorkplaceInactive(
  workplaceId: string | undefined,
  statusMap: Map<string, string>
): boolean {
  if (!isDefined(workplaceId)) {
    return false;
  }

  const status = statusMap.get(workplaceId);

  // If we don't have status info for this workplace, don't filter it out
  if (!isDefined(status)) {
    return false;
  }

  return INACTIVE_WORKPLACE_STATUSES.has(status);
}

interface UseFilterChannelsByWorkplaceStatusProps<T extends ChatChannel> {
  channels: T[];
  enabled?: boolean;
}

interface UseFilterChannelsByWorkplaceStatusResult<T extends ChatChannel> {
  filteredChannels: T[];
  isLoading: boolean;
}

/**
 * Hook to filter out chat channels for workplaces with inactive statuses.
 *
 * This hook fetches workplace statuses for all channels and filters out
 * channels where the workplace has a SUSPENDED, TERMINATED, or ARCHIVED status.
 *
 * @param props.channels - The list of chat channels to filter
 * @param props.enabled - Whether to enable the filtering (default: true)
 * @returns The filtered channels and loading state
 */
export function useFilterChannelsByWorkplaceStatus<T extends ChatChannel>(
  props: UseFilterChannelsByWorkplaceStatusProps<T>
): UseFilterChannelsByWorkplaceStatusResult<T> {
  const { channels, enabled = true } = props;

  const workplaceIds = useMemo(() => getUniqueWorkplaceIds(channels), [channels]);

  const { data: workplacesData, isLoading } = useGetWorkplacesById(
    { workplaceIds },
    { enabled: enabled && workplaceIds.length > 0 }
  );

  const filteredChannels = useMemo(() => {
    if (!enabled || isLoading || !isDefined(workplacesData)) {
      return channels;
    }

    // Flatten all pages of workplace data
    const allWorkplaces = workplacesData.pages.flatMap((page) => page.data);
    const statusMap = createWorkplaceStatusMap(allWorkplaces);

    return channels.filter((channel) => {
      const workplaceId = getWorkplaceIdFromChannel(channel);
      return !isWorkplaceInactive(workplaceId, statusMap);
    });
  }, [channels, enabled, isLoading, workplacesData]);

  return {
    filteredChannels,
    isLoading,
  };
}
